import Layout from "../../components/Layout.tsx";
import { Field, FieldGroup, Label } from "../../components/Fieldset.tsx";
import { Select } from "../../components/Select.tsx";
import { Input } from "../../components/Input.tsx";
import { Checkbox, CheckboxField } from "../../components/Checkbox.tsx";
import { StepProps } from "./AddNewConnection.tsx";
import { getFrequentieData, getDayList, getFrequencyLabel } from "../../hooks/data.ts";
import { useNewConnectionContext } from "../../context/NewConnectionContext.tsx";
import { RunFrequency } from "../../types.ts";
import { useState } from "react";

import React from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

const DEFAULT_LOOKBACK_WEEKS = 12;
const MIN_LOOKBACK_WEEKS = 1;
const MAX_LOOKBACK_WEEKS = 52;


export interface SelectMonthdayFieldProps {
  runDay: number;
  setSelectedDay: React.Dispatch<React.SetStateAction<number>>;
}

export const SelectMonthdayField: React.FC<SelectMonthdayFieldProps> = ({ runDay, setSelectedDay }) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const lastDayOfCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const isLastDaySelected = runDay === 31;
  const selectedDate = runDay > 0
    ? new Date(currentYear, currentMonth, Math.min(runDay, lastDayOfCurrentMonth))
    : undefined;

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setSelectedDay(31)}
        className={`flex w-full max-w-xl items-start justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${isLastDaySelected
          ? "border-blue-200 bg-blue-50 text-blue-900"
          : "border-gray-200 bg-white text-gray-900 hover:border-blue-200 hover:bg-blue-50/50"
          }`}
      >
        <div>
          <p className="text-sm font-medium">Laatste dag van de maand</p>
          <p className="text-sm text-gray-500">
            Gebruik dag 31 als vaste keuze voor maandafsluiting.
          </p>
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-blue-700">Dag 31</span>
      </button>

      <div className="max-w-xs">
        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={(date) => {
            if (date == null) {
              setSelectedDay(0);
              return;
            }
            setSelectedDay(date.getDate());
          }}
          showOutsideDays={false}
        />
      </div>

      {isLastDaySelected && (
        <div className="max-w-xl rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Deze maandelijkse planning draait steeds op de laatste kalenderdag van de maand.
        </div>
      )}
    </div>
  );
};

export interface SelectWeekdayFieldProps {
  runDay?: number;
  setRunDay: React.Dispatch<React.SetStateAction<number>>;
}

export const SelectWeekdayField: React.FC<SelectWeekdayFieldProps> = ({ runDay, setRunDay }) => {
  const { dayList } = getDayList();

  return (
    <Field>
      {dayList.map(({ id, label }) => (
        <CheckboxField key={id} className="flex items-center gap-3">
          <input
            type="radio"
            name="weekday"
            value={id}
            checked={runDay === id}
            onChange={() => setRunDay(id)}
          />
          <Label htmlFor={id.toString()}>{label}</Label>
        </CheckboxField>
      ))}
    </Field>
  );
}

const SynchronisationTimeStep = ({ handleButtonBackClick, handleButtonNextClick, stepIndicator }: StepProps) => {
  const { newConnectionData, dispatch } = useNewConnectionContext();
  const { frequentieData } = getFrequentieData();
  const schema = newConnectionData.synchronisationSchema;

  // Use existing values from context if available, otherwise use defaults
  const [runFrequency, setRunFrequency] = useState<RunFrequency>(
    (schema?.runFrequency as RunFrequency) || RunFrequency.WEEKLY
  );
  const [runDay, setRunDay] = useState<number>(schema?.runDay || 0);
  const [runHour, setRunHour] = useState<number>(schema?.runHour ?? 0);
  const [runMinute, setRunMinute] = useState<number>(schema?.runMinute ?? 0);
  const [lookbackWeeks, setLookbackWeeks] = useState<number>(
    schema?.lookbackWeeks ?? DEFAULT_LOOKBACK_WEEKS
  );
  const [dryRun, setDryRun] = useState<boolean>(schema?.dryRun ?? false);
  const [isPaused, setIsPaused] = useState<boolean>(schema?.isPaused ?? true);

  // Format for <input type="time"> (HH:MM)
  const timeValue = `${runHour.toString().padStart(2, "0")}:${runMinute.toString().padStart(2, "0")}`;

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parts = e.target.value.split(":");
    if (parts.length !== 2) return;
    const [hour, minute] = parts.map(Number);
    if (isNaN(hour) || isNaN(minute)) return;
    setRunHour(hour);
    setRunMinute(minute);
  };

  const handleLookbackWeeksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = Number(e.target.value);
    if (Number.isNaN(nextValue)) {
      return;
    }

    setLookbackWeeks(Math.min(MAX_LOOKBACK_WEEKS, Math.max(MIN_LOOKBACK_WEEKS, nextValue)));
  };

  const handleNextClick = () => {
    dispatch({
      type: "SET_SYNCHRONISATION_TIME",
      synchronisationTime: {
        runFrequency,
        runDay,
        runHour,
        runMinute,
        lookbackWeeks,
        dryRun,
        isPaused,
      }
    });
    handleButtonNextClick?.();
  };

  const isUpdating = newConnectionData.id && newConnectionData.id > 0;

  return (
    <Layout
      title={isUpdating ? "Koppeling aanpassen" : "Nieuwe koppeling maken"}
      stepTitle="Synchronisatieschema"
      onButtonBackClick={handleButtonBackClick}
      onButtonNextClick={handleNextClick}
      stepIndicator={stepIndicator}
    >
      <FieldGroup>
        <Field className="items-baseline sm:grid sm:grid-cols-4">
          <Label>Synchronisatiefrequentie</Label>
          <Select
            className="sm:col-span-3 max-w-xs"
            id="frequentie"
            name="frequentie"
            value={runFrequency}
            onChange={(e) => {
              const freq = e.target.value as RunFrequency;
              setRunFrequency(freq);
              if (freq === RunFrequency.DAILY) {
                setRunDay(0);
              }
            }}>
            {frequentieData.map((freq, index) => (
              <option key={index} value={freq}>
                {getFrequencyLabel(freq)}
              </option>
            ))}
          </Select>
        </Field>
        {runFrequency === RunFrequency.WEEKLY && (
          <SelectWeekdayField runDay={runDay} setRunDay={setRunDay} />
        )}
        {runFrequency === RunFrequency.MONTHLY && (
          <SelectMonthdayField
            runDay={runDay}
            setSelectedDay={setRunDay}
          />
        )}
        <Field className="items-baseline sm:grid sm:grid-cols-4">
          <Label>Tijd</Label>
          <Input
            type="time"
            className="sm:col-span-3 max-w-xs"
            id="tijd"
            name="tijd"
            value={timeValue}
            onChange={handleTimeChange}
          />
        </Field>
        <Field className="items-baseline sm:grid sm:grid-cols-4">
          <Label>Terugkijken</Label>
          <div className="sm:col-span-3 max-w-xs">
            <Input
              type="number"
              min={MIN_LOOKBACK_WEEKS}
              max={MAX_LOOKBACK_WEEKS}
              id="lookback-weeks"
              name="lookback-weeks"
              value={lookbackWeeks}
              onChange={handleLookbackWeeksChange}
            />
            <p className="mt-2 text-sm text-gray-500">
              We controleren de laatste {lookbackWeeks} ISO-weken voor nieuw te verwerken posten sinds de laatste synchronisatie.
            </p>
          </div>
        </Field>
        <Field className="items-start sm:grid sm:grid-cols-4">
          <Label>Geplande uitvoering</Label>
          <div
            className="sm:col-span-3 max-w-xl rounded-xl border border-gray-200 bg-gray-50 px-4 py-3"
            onClick={() => setDryRun((current) => !current)}
          >
            <div className="flex items-start gap-3 cursor-pointer select-none">
              <span onClick={(event) => event.stopPropagation()}>
                <Checkbox color="primary" checked={dryRun} onChange={setDryRun} />
              </span>
              <div>
                <p className="text-sm font-medium text-gray-900">Alleen proefdraai uitvoeren</p>
                <p className="text-sm text-gray-500">
                  Geplande runs halen gegevens op uit Easyflex, maar boeken niets in Exact Online.
                </p>
              </div>
            </div>
          </div>
        </Field>

        {isUpdating && (
          <Field className="items-start sm:grid sm:grid-cols-4">
            <Label>Automatische synchronisatie</Label>
            <div
              className="sm:col-span-3 max-w-xl rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 cursor-pointer"
              onClick={() => setIsPaused((current) => !current)}
            >
              <div className="flex items-start gap-3 select-none">
                <span onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    color="primary"
                    checked={isPaused}
                    onChange={setIsPaused}
                  />
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900">Automatische synchronisatie pauzeren</p>
                  <p className="text-sm text-gray-500">
                    Handmatige synchronisaties blijven mogelijk terwijl de automatische synchronisatie gepauzeerd is.
                  </p>
                </div>
              </div>
            </div>
          </Field>
        )}
      </FieldGroup>
    </Layout>
  );
};

export default SynchronisationTimeStep;
